import { inject, observer } from "mobx-react";
import * as React from "react";
import { HorseForSaleEndpoints } from "src/endpoints/HorseForSaleEndpoints";
import { WithdrawalOutcomeRequest } from "src/requests/HorseForSaleRequests";

import { faDollarSign, faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    AllPostApprovedStates,
    HorseForSaleState,
    PreDecisionActiveStates,
    PreHipNumberActiveStates,
    WithdrawalOutcome
} from "../../model/HorseForSale";
import { HorseForSaleStore } from "../../stores/HorseForSaleStore";
import EditableCard, { EditableTextareaGroup } from "../common/EditableCard";
import { FormDialog } from "../common/FormDialog";
import { SimpleConfirmation, SimpleAlert } from "../common/SimpleConfirmAlert";
import { InputGroup } from "../common/SimpleFormComponents";
import { errorMessage, successMessage } from "../common/Toast";
import { ConvertToCents } from "src/common/Methods/FileUtil";
import Withdrawal from "./Withdrawal";

//Props = PropsStore + PropsComponents
export interface PropsStores {
    horseForSaleStore?: HorseForSaleStore;
    horseForSaleEndpoints?: HorseForSaleEndpoints;
}
export interface PropsComponent {}
type Props = PropsStores & PropsComponent;

//State = StateComponent + State Editable Field
export interface StateComponent {
    isEditable: boolean;
    wasValidated: boolean;
}
export interface StateFields {
    newState: HorseForSaleState | null;
    approvalNotes: string;
}
type State = StateComponent & StateFields;

@inject("horseForSaleEndpoints")
@inject("horseForSaleStore")
@observer
export default class SelectionAndWithdrawal extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            isEditable: false,
            wasValidated: false,
            newState: null,
            approvalNotes: ""
        };
    }

    generateEntryFeeInvoice = async (type: "NEW" | "UPDATE") => {
        try {
            const horseForSaleStore = this.props.horseForSaleStore!;
            const hfs = horseForSaleStore.horseForSale!;

            if (hfs.owners.length === 0) {
                SimpleAlert("Invoices cannot be generated: this Horse-for-Sale currently does not have any owners registered.");
                return;
            }

            // TODO: Confirm that the "NEW" check below shouldn't actually be the opposite
            // (since voiding likely happens when updating)
            const goAhead = await SimpleConfirmation(
                `Are you sure you'd like to ${type === "NEW" ? `generate` : `update`} the Entry Fee Invoice for ${hfs.horse.name}?
                ${type === "NEW" ? `` : ` Existing invoices will be voided.`}
            `
            );
            if (!goAhead) {
                return;
            }

            await this.props.horseForSaleStore!.generateEntryFeeInvoice(hfs.id);
            successMessage(`Invoice successfully generated.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    editFieds = () => {
        this.setState(this.copyHorseForSaleToState({ isEditable: true }) as any);
    };

    saveEditableFields = () => {
        const horseForSaleStore = this.props.horseForSaleStore;
        if (!horseForSaleStore) {
            throw new Error(" ** No Horse For Sale Store **");
        }

        const horseForSale = horseForSaleStore.horseForSale;
        if (!horseForSale) {
            throw new Error(" ** No Horse For Sale **");
        }

        if (!this.state.newState) {
            throw new Error("Cannot save an empty state");
        }

        horseForSaleStore
            .approveOrRejectEntryHorseForSale(this.state.newState, this.state.approvalNotes)
            .then(() => this.setState({ isEditable: false }))
            .catch(err => alert(err));
    };

    cancelEditableFields = () => {
        this.setState({ isEditable: false });
    };

    handleOnChange = (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>
    ): void => {
        event.preventDefault();

        const target = event.target;
        const value = target.value;
        const name = target.name;

        const state = this.state;
        state[name] = value;
        this.setState(state);
    };

    handleApproveOrRejectEntry = (e: any) => {
        const newState = e.currentTarget.value;
        this.setState({ newState });
    };

    submit = () => {
        if (this.validateForm()) {
            this.setState({ wasValidated: false });
            this.saveEditableFields();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    validateForm = (): boolean => {
        return this.state.newState === HorseForSaleState.APPROVED || this.state.newState === HorseForSaleState.REJECTED ? true : false;
    };

    render() {
        const horseForSaleStore = this.props.horseForSaleStore;
        if (!horseForSaleStore) {
            return <div />;
        }

        const horseForSale = horseForSaleStore.horseForSale;
        if (!horseForSale) {
            return <div />;
        }

        const approvedAtSomePoint = AllPostApprovedStates().some(s => s === horseForSale.state);

        const editingState = this.state.isEditable ? this.state.newState : horseForSale.state;
        const approvedChecked = this.state.isEditable ? AllPostApprovedStates().some(s => s === this.state.newState) : approvedAtSomePoint;

        const isANewEntryFeeInvoice = horseForSale.owners && horseForSale.owners.some(owner => owner.entryFeeInvoice === null);

        return (
            <>
                <EditableCard
                    title="Selection"
                    // Only allow editing if hip numbers haven't been generated yet and if we're in an editable state
                    // (Note that withdrawn states cannot be reversed, but REJECTED can)
                    hideEditButton={
                        horseForSale.hipNumber !== null ||
                        ![...PreHipNumberActiveStates(), HorseForSaleState.REJECTED].some(s => s === horseForSale.state)
                    }
                    editable={this.state.isEditable}
                    onEdit={this.editFieds}
                    onCancel={this.cancelEditableFields}
                    onSave={this.submit}
                    otherHeaderButtons={
                        <button
                            className="btn btn-info btn-sm mr-3"
                            onClick={() => this.generateEntryFeeInvoice(isANewEntryFeeInvoice ? "NEW" : "UPDATE")}
                            // This operation is available for all post-approval states
                            disabled={!AllPostApprovedStates().some(s => s === horseForSale.state)}
                        >
                            <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" />
                            {isANewEntryFeeInvoice ? "Generate " : "Update "}
                            invoice for Entry Fee
                        </button>
                    }
                >
                    <form id="selection-form">
                        {this.state.wasValidated && (
                            <div className="form-row my-3">
                                <div className="col-md-12 alert alert-danger text-center" role="alert">
                                    Please select Approve or Reject
                                </div>
                            </div>
                        )}
                        <div className="form-row">
                            <div className="form-group col-sm-12 col-md-4">
                                <label htmlFor="" className="d-block">
                                    Approve or Reject Entry: <span className="text-danger">*</span>
                                </label>
                                <div className="btn-group btn-group-toggle disabled" data-toggle="buttons">
                                    <label
                                        className={
                                            "btn " +
                                            (approvedChecked ? "btn-info" : "btn-outline-info") +
                                            (!this.state.isEditable ? " disabled " : "")
                                        }
                                    >
                                        <input
                                            type="radio"
                                            name="newState"
                                            value={HorseForSaleState.APPROVED}
                                            checked={approvedChecked}
                                            onChange={this.handleApproveOrRejectEntry}
                                            disabled={!this.state.isEditable}
                                        />{" "}
                                        Approved
                                    </label>
                                    <label
                                        className={
                                            "btn " +
                                            (editingState === HorseForSaleState.REJECTED ? "btn-info" : "btn-outline-info") +
                                            (!this.state.isEditable ? " disabled " : "")
                                        }
                                    >
                                        <input
                                            type="radio"
                                            name="newState"
                                            value={HorseForSaleState.REJECTED}
                                            checked={editingState === HorseForSaleState.REJECTED}
                                            onChange={this.handleApproveOrRejectEntry}
                                            disabled={!this.state.isEditable}
                                        />{" "}
                                        Rejected
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="form-row">
                            <EditableTextareaGroup
                                label="Notes"
                                rows={5}
                                name="approvalNotes"
                                className="col-md-12"
                                inputClassName="not-required"
                                editable={this.state.isEditable}
                                editableValue={this.state.approvalNotes || ""}
                                nonEditableValue={horseForSale.approvalNotes || ""}
                                onChange={this.handleOnChange}
                            />
                        </div>
                    </form>
                </EditableCard>

                <Withdrawal />
            </>
        );
    }

    private copyHorseForSaleToState(otherFields: Partial<State> = {}): Partial<State> {
        const horseForSale = this.props.horseForSaleStore!.horseForSale;
        if (!horseForSale) {
            return {};
        }

        const updatedState: Partial<State> = Object.assign(
            {
                newState: horseForSale.state || "",
                approvalNotes: horseForSale.approvalNotes || ""
            },
            otherFields
        );

        return updatedState;
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/SelectionAndWithdrawal.tsx